import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Button, 
  Upload, 
  message, 
  Alert, 
  Divider, 
  Select,
  Steps,
  Space,
  Table,
  Typography,
  Collapse,
  Card,
  Spin,
  Switch,
  Statistic,
  Radio
} from 'antd';
import { 
  InboxOutlined, 
  ShopOutlined, 
  UploadOutlined, 
  SyncOutlined, 
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { ordersService } from '../../services/ordersService';
import { supabase } from '../../utils/supabase';
import { integrationsApi } from '../../utils/api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Step } = Steps;
const { Panel } = Collapse;
const { Dragger } = Upload;

const ImportOrdersModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [importMethod, setImportMethod] = useState('api');
  const [previewData, setPreviewData] = useState([]);
  const [importStatus, setImportStatus] = useState({
    total: 0,
    processed: 0,
    success: 0,
    errors: 0,
    inProgress: false
  });
  const [shopifyCredentials, setShopifyCredentials] = useState({
    shopName: '',
    apiKey: '',
    password: '',
    apiVersion: '2025-01'
  });
  const [savedCredentials, setSavedCredentials] = useState(null);
  const [credentialsSource, setCredentialsSource] = useState('saved');
  const [loadingSavedCredentials, setLoadingSavedCredentials] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [mapFields, setMapFields] = useState({
    order_number: 'order_number',
    customer_name: 'customer_name',
    customer_phone: 'customer_phone',
    delivery_address: 'delivery_address',
    total_amount: 'total_amount',
    status: 'status',
    created_at: 'created_at'
  });
  const [previewError, setPreviewError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importStats, setImportStats] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [shopifyFilters, setShopifyFilters] = useState({
    status: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    if (visible) {
      loadSavedCredentials();
    }
  }, [visible]);

  const loadSavedCredentials = async () => {
    try {
      setLoadingSavedCredentials(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'shopify')
        .single();

      if (error && error.code !== 'PGSQL_ERROR_NO_DATA_FOUND') {
        throw error;
      }

      if (data && data.is_active && data.api_key && data.api_password) {
        setSavedCredentials({
          shopName: data.shop_name,
          apiKey: data.api_key,
          password: data.api_password,
          apiVersion: data.api_version || '2023-04'
        });

        if (credentialsSource === 'saved') {
          setShopifyCredentials({
            shopName: data.shop_name,
            apiKey: '••••••••••••••••', // Masqué pour la sécurité
            password: '••••••••••••••••', // Masqué pour la sécurité
            apiVersion: data.api_version || '2023-04'
          });
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des identifiants Shopify:', error);
    } finally {
      setLoadingSavedCredentials(false);
    }
  };

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const resetForm = () => {
    form.resetFields();
    setCurrent(0);
    setPreviewData([]);
    setImportStatus({
      total: 0,
      processed: 0,
      success: 0,
      errors: 0,
      inProgress: false
    });
    setCsvFile(null);
    
    // Réinitialiser les identifiants selon la source sélectionnée
    if (credentialsSource === 'saved' && savedCredentials) {
      setShopifyCredentials({
        shopName: savedCredentials.shopName,
        apiKey: '••••••••••••••••',
        password: '••••••••••••••••',
        apiVersion: savedCredentials.apiVersion
      });
    } else {
      setShopifyCredentials({
        shopName: '',
        apiKey: '',
        password: '',
        apiVersion: '2023-04'
      });
    }
  };

  const handleMethodChange = (method) => {
    setImportMethod(method);
  };

  const handleCredentialsSourceChange = (e) => {
    const source = e.target.value;
    setCredentialsSource(source);
    
    if (source === 'saved' && savedCredentials) {
      setShopifyCredentials({
        shopName: savedCredentials.shopName,
        apiKey: '••••••••••••••••',
        password: '••••••••••••••••',
        apiVersion: savedCredentials.apiVersion
      });
    } else {
      setShopifyCredentials({
        shopName: '',
        apiKey: '',
        password: '',
        apiVersion: '2023-04'
      });
    }
  };

  const handleShopifyCredentialsChange = (field, value) => {
    setShopifyCredentials({
      ...shopifyCredentials,
      [field]: value
    });
  };

  const handleSkipDuplicatesChange = (checked) => {
    setSkipDuplicates(checked);
  };

  const handleMapFieldChange = (field, value) => {
    setMapFields({
      ...mapFields,
      [field]: value
    });
  };

  const handleTestConnection = async () => {
    setLoading(true);
    try {
      // Préparer les identifiants à envoyer
      let credentials = { ...shopifyCredentials };
      
      // Si on utilise les identifiants sauvegardés et qu'ils sont masqués
      if (credentialsSource === 'saved' && savedCredentials &&
          shopifyCredentials.apiKey.includes('•')) {
        credentials = { ...savedCredentials };
      }
      
      // Tester la connexion
      const result = await ordersService.testShopifyConnection(credentials);
      message.success('Connexion réussie à la boutique Shopify !');
    } catch (error) {
      message.error('Erreur de connexion à Shopify. Vérifiez vos identifiants.');
      console.error('Erreur de connexion à Shopify:', error);
    } finally {
      setLoading(false);
    }
  };

  const previewOrders = async () => {
    try {
      setLoading(true);
      setPreviewError(null);
      setPreviewData(null);

      // Préparer les identifiants pour la requête
      const credentials = {
        shopName: shopifyCredentials.shopName,
        apiKey: shopifyCredentials.apiKey,
        apiPassword: shopifyCredentials.password,
        apiVersion: shopifyCredentials.apiVersion
      };

      // Préparer les filtres
      const filters = {
        status: shopifyFilters.status,
        since: shopifyFilters.startDate ? new Date(shopifyFilters.startDate).toISOString() : null,
        until: shopifyFilters.endDate ? new Date(shopifyFilters.endDate).toISOString() : null
      };

      // Utiliser le service API pour obtenir la prévisualisation
      const response = await integrationsApi.previewShopifyOrders(credentials, filters);
      const data = await response.data;

      
      console.log('data', data);
      setPreviewData(data);
      
      // Vérifier s'il y a des doublons
      if (data.orders && data.orders.length > 0) {
        const orderIds = data.orders.map(order => order.order_id);
        const duplicatesResponse = await integrationsApi.checkDuplicateOrders(orderIds);
        const duplicatesData = await duplicatesResponse.data;
        
        if (duplicatesResponse.ok && duplicatesData.duplicates) {
          // Marquer les commandes qui sont des doublons
          const updatedOrders = data.orders.map(order => ({
            ...order,
            isDuplicate: duplicatesData.duplicates.includes(order.order_id)
          }));
          
          setPreviewData({
            ...data,
            orders: updatedOrders,
            duplicateCount: duplicatesData.count
          });
        }
      }
    } catch (error) {
      console.error('Erreur lors de la prévisualisation:', error);
      setPreviewError(error.message || 'Erreur lors de la prévisualisation');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCredentials = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Ne sauvegarde pas si les champs sont masqués (contiennent des •)
      if (shopifyCredentials.apiKey.includes('•') || shopifyCredentials.password.includes('•')) {
        message.warning('Les identifiants masqués ne peuvent pas être sauvegardés. Veuillez les saisir à nouveau.');
        return;
      }

      const { error } = await supabase
        .from('settings')
        .upsert({
          user_id: user.id,
          type: 'shopify',
          shop_name: shopifyCredentials.shopName,
          api_key: shopifyCredentials.apiKey,
          api_password: shopifyCredentials.password,
          api_version: shopifyCredentials.apiVersion,
          is_active: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,type'
        });

      if (error) throw error;

      message.success('Identifiants Shopify sauvegardés avec succès');
      await loadSavedCredentials();
      setCredentialsSource('saved');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des identifiants:', error);
      message.error('Erreur lors de la sauvegarde des identifiants Shopify');
    } finally {
      setLoading(false);
    }
  };

  const importOrders = async () => {
    try {
      setImporting(true);
      setImportError(null);
      setImportStats(null);

      // Filtrer les commandes sélectionnées (ou toutes si aucune sélection)
      const ordersToImport = selectedRowKeys.length > 0
        ? previewData.orders.filter(order => selectedRowKeys.includes(order.order_id))
        : previewData.orders;

      // Filtrer pour exclure les doublons si demandé
      const filteredOrders = skipDuplicates
        ? ordersToImport.filter(order => !order.isDuplicate)
        : ordersToImport;

      if (filteredOrders.length === 0) {
        throw new Error('Aucune commande à importer après filtrage');
      }

      // Préparer les identifiants pour la requête
      const credentials = {
        shopName: shopifyCredentials.shopName,
        apiKey: shopifyCredentials.apiKey,
        apiPassword: shopifyCredentials.password,
        apiVersion: shopifyCredentials.apiVersion
      };

      // Utiliser le service API pour importer les commandes
      const response = await integrationsApi.importShopifyOrders(credentials, filteredOrders);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erreur lors de l\'importation');
      }

      setImportStats(data.stats);
      
      // Enregistrer les nouveaux identifiants si demandé
      if (credentialsSource === 'new' && savedCredentials) {
        await handleSaveCredentials();
      }
      
      // Réinitialiser après une importation réussie
      setTimeout(() => {
        setPreviewData(null);
        setSelectedRowKeys([]);
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de l\'importation:', error);
      setImportError(error.message || 'Erreur lors de l\'importation');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onCancel();
  };

  const handleFinish = () => {
    message.success(`${importStatus.success} commandes importées avec succès.`);
    resetForm();
    onSuccess && onSuccess();
    onCancel();
  };

  const columns = [
    {
      title: 'Numéro',
      dataIndex: 'order_number',
      key: 'order_number',
    },
    {
      title: 'Client',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Adresse',
      dataIndex: 'delivery_address',
      key: 'delivery_address',
      ellipsis: true,
    },
    {
      title: 'Montant',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => `${amount.toFixed(2)} €`,
    },
  ];

  const steps = [
    {
      title: 'Source',
      content: (
        <div>
          <Title level={5}>Sélectionnez une source d'importation</Title>
          
          <div className="mb-4">
            <Select
              style={{ width: '100%' }}
              value={importMethod}
              onChange={handleMethodChange}
            >
              <Option value="api">API Shopify</Option>
              <Option value="csv">Fichier CSV</Option>
            </Select>
          </div>
          
          {importMethod === 'api' ? (
            <>
              <Card className="mb-4">
                <Radio.Group 
                  onChange={handleCredentialsSourceChange} 
                  value={credentialsSource}
                  disabled={!savedCredentials}
                  className="mb-4"
                >
                  <Space direction="vertical">
                    <Radio value="saved" disabled={!savedCredentials}>
                      Utiliser les identifiants sauvegardés
                      {!savedCredentials && (
                        <Text type="secondary" className="ml-2">
                          (Aucun identifiant sauvegardé)
                        </Text>
                      )}
                      {loadingSavedCredentials && (
                        <Spin size="small" className="ml-2" />
                      )}
                    </Radio>
                    <Radio value="new">Saisir de nouveaux identifiants</Radio>
                  </Space>
                </Radio.Group>

                {credentialsSource === 'saved' && !savedCredentials && (
                  <Alert
                    message="Aucun identifiant sauvegardé"
                    description="Vous n'avez pas encore sauvegardé d'identifiants Shopify. Veuillez en saisir de nouveaux ou configurer l'intégration Shopify dans les paramètres."
                    type="info"
                    showIcon
                    className="mb-4"
                  />
                )}
              </Card>

              <Card title="Connexion à Shopify" className="mb-4">
                <Alert
                  message="Note sur les abonnements Shopify"
                  description={
                    <div>
                      <p>L'importation de l'historique des commandes et des informations détaillées des clients via cette interface nécessite un abonnement <strong>Shopify</strong> standard ou supérieur.</p>
                      <p>Avec un abonnement <strong>Shopify Basic</strong>, vous ne pourrez pas utiliser cette fonctionnalité d'import historique, mais les nouvelles commandes peuvent toujours être reçues automatiquement via webhooks.</p>
                    </div>
                  }
                  type="info"
                  showIcon
                  className="mb-4"
                />
                <Form layout="vertical">
                  <Form.Item
                    label="Nom de la boutique"
                    required
                  >
                    <Input 
                      placeholder="votre-boutique" 
                      value={shopifyCredentials.shopName}
                      onChange={(e) => handleShopifyCredentialsChange('shopName', e.target.value)}
                      addonAfter=".myshopify.com"
                      disabled={credentialsSource === 'saved' && savedCredentials}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    label="Clé API"
                    required
                  >
                    <Input 
                      placeholder="Clé API Shopify" 
                      value={shopifyCredentials.apiKey}
                      onChange={(e) => handleShopifyCredentialsChange('apiKey', e.target.value)}
                      disabled={credentialsSource === 'saved' && savedCredentials}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    label="Mot de passe API"
                    required
                  >
                    <Input.Password 
                      placeholder="Mot de passe API" 
                      value={shopifyCredentials.password}
                      onChange={(e) => handleShopifyCredentialsChange('password', e.target.value)}
                      disabled={credentialsSource === 'saved' && savedCredentials}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    label="Version API"
                  >
                    <Input 
                      placeholder="2025-01" 
                      value={shopifyCredentials.apiVersion}
                      onChange={(e) => handleShopifyCredentialsChange('apiVersion', e.target.value)}
                      disabled={credentialsSource === 'saved' && savedCredentials}
                    />
                  </Form.Item>
                  
                  <Space>
                    <Button 
                      type="primary" 
                      onClick={handleTestConnection}
                      loading={loading}
                    >
                      Tester la connexion
                    </Button>
                    
                    {credentialsSource === 'new' && (
                      <Button
                        icon={<SaveOutlined />}
                        onClick={handleSaveCredentials}
                        loading={loading}
                      >
                        Sauvegarder ces identifiants
                      </Button>
                    )}
                  </Space>
                </Form>
              </Card>
            </>
          ) : (
            <Card title="Importation par fichier CSV" className="mb-4">
              <Dragger
                accept=".csv"
                beforeUpload={(file) => {
                  setCsvFile(file);
                  return false;
                }}
                fileList={csvFile ? [csvFile] : []}
                onRemove={() => setCsvFile(null)}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Cliquez ou glissez-déposez un fichier CSV</p>
                <p className="ant-upload-hint">
                  Le fichier doit contenir les commandes exportées depuis Shopify
                </p>
              </Dragger>
              
              <div className="mt-4">
                <Alert
                  message="Format attendu"
                  description="Le fichier CSV doit contenir au minimum les colonnes suivantes : numéro de commande, nom du client, téléphone, adresse, montant total, statut et date de création."
                  type="info"
                  showIcon
                />
              </div>
            </Card>
          )}
          
          <Collapse ghost>
            <Panel header="Options avancées" key="1">
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <Text>Ignorer les doublons</Text>
                  <Switch 
                    checked={skipDuplicates} 
                    onChange={handleSkipDuplicatesChange}
                  />
                </div>
                <Text type="secondary">
                  Activer cette option pour éviter d'importer des commandes déjà existantes
                </Text>
              </div>
              
              <Title level={5}>Mapping des champs</Title>
              <Paragraph type="secondary" className="mb-4">
                Définissez comment les champs Shopify correspondent aux champs de votre système
              </Paragraph>
              
              <div className="grid grid-cols-2 gap-4">
                <Form.Item label="Numéro de commande">
                  <Select
                    value={mapFields.order_number}
                    onChange={(value) => handleMapFieldChange('order_number', value)}
                  >
                    <Option value="order_number">Numéro de commande</Option>
                    <Option value="name">Nom de la commande</Option>
                    <Option value="id">ID de la commande</Option>
                  </Select>
                </Form.Item>
                
                <Form.Item label="Nom du client">
                  <Select
                    value={mapFields.customer_name}
                    onChange={(value) => handleMapFieldChange('customer_name', value)}
                  >
                    <Option value="customer_name">Nom du client</Option>
                    <Option value="shipping_name">Nom de livraison</Option>
                    <Option value="billing_name">Nom de facturation</Option>
                  </Select>
                </Form.Item>
                
                <Form.Item label="Téléphone">
                  <Select
                    value={mapFields.customer_phone}
                    onChange={(value) => handleMapFieldChange('customer_phone', value)}
                  >
                    <Option value="customer_phone">Téléphone</Option>
                    <Option value="shipping_phone">Téléphone de livraison</Option>
                    <Option value="billing_phone">Téléphone de facturation</Option>
                  </Select>
                </Form.Item>
                
                <Form.Item label="Adresse">
                  <Select
                    value={mapFields.delivery_address}
                    onChange={(value) => handleMapFieldChange('delivery_address', value)}
                  >
                    <Option value="delivery_address">Adresse de livraison</Option>
                    <Option value="shipping_address">Adresse d'expédition</Option>
                    <Option value="billing_address">Adresse de facturation</Option>
                  </Select>
                </Form.Item>
              </div>
            </Panel>
          </Collapse>
        </div>
      )
    },
    {
      title: 'Prévisualisation',
      content: (
        <div>
          <Title level={5}>Prévisualisation des données à importer</Title>
          
          <Alert
            message={`${previewData?.orders?.length || 0} commandes prêtes à être importées${previewData?.duplicateCount ? ` (${previewData?.duplicateCount} doublons détectés)` : ''}`}
            type="info"
            showIcon
            className="mb-4"
          />
          
          {previewData?.duplicateCount > 0 && (
            <Alert
              message="Doublons détectés"
              description={`${previewData.duplicateCount} commandes existent déjà dans votre système. Si vous souhaitez les ignorer, activez l'option "Ignorer les doublons" dans les options avancées.`}
              type="warning"
              showIcon
              className="mb-4"
            />
          )}
          
          <Table
            columns={columns}
            dataSource={previewData?.orders || []}
            rowKey="order_id"
            size="small"
            pagination={{ pageSize: 5 }}
            scroll={{ x: true }}
            rowSelection={{
              selectedRowKeys: selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
          />
          
          <Alert
            message="Vérification"
            description="Vérifiez que les données sont correctes avant de procéder à l'importation. Les commandes seront ajoutées à votre système avec le statut 'En attente'."
            type="warning"
            showIcon
            className="mt-4"
          />
        </div>
      )
    },
    {
      title: 'Importation',
      content: (
        <div>
          <Title level={5}>Progression de l'importation</Title>
          
          <div className="text-center mb-6">
            {importing ? (
              <Spin size="large" tip="Importation en cours..." />
            ) : (
              <Button
                type="primary"
                size="large"
                icon={<SyncOutlined />}
                onClick={importOrders}
                disabled={importing || previewData?.orders?.length === 0}
              >
                Démarrer l'importation
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <Card>
              <Statistic
                title="Commandes traitées"
                value={importStatus.processed}
                suffix={`/ ${importStatus.total}`}
              />
            </Card>
            <Card>
              <Statistic
                title="Importées avec succès"
                value={importStatus.success}
                valueStyle={{ color: '#3f8600' }}
                suffix={importStatus.total > 0 ? `(${Math.round((importStatus.success / importStatus.total) * 100)}%)` : ''}
              />
            </Card>
          </div>
          
          {importError && (
            <Alert
              message={importError}
              description="Erreur lors de l'importation. Vérifiez les logs pour plus d'informations."
              type="error"
              showIcon
              className="mb-4"
            />
          )}
          
          {importStatus.processed === importStatus.total && importStatus.total > 0 && (
            <Alert
              message="Importation terminée"
              description={`${importStatus.success} commandes importées avec succès.`}
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
            />
          )}
        </div>
      )
    },
    {
      title: 'Résumé',
      content: (
        <div>
          <Title level={5}>Résumé de l'importation</Title>
          
          <div className="text-center mb-6">
            <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
            <Title level={3} className="mt-2">Importation terminée</Title>
          </div>
          
          <Alert
            message="Résumé"
            description={
              <div>
                <p><strong>Total des commandes :</strong> {importStatus.total}</p>
                <p><strong>Commandes importées avec succès :</strong> {importStatus.success}</p>
                <p><strong>Commandes avec erreurs :</strong> {importStatus.errors}</p>
              </div>
            }
            type="info"
            showIcon
            className="mb-4"
          />
          
          <div className="text-center mt-6">
            <Title level={5}>Que souhaitez-vous faire maintenant ?</Title>
            <Space>
              <Button type="primary" onClick={handleFinish}>
                Terminer
              </Button>
              <Button onClick={resetForm}>
                Nouvelle importation
              </Button>
            </Space>
          </div>
        </div>
      )
    },
  ];

  const modalFooter = [
    <Button key="cancel" onClick={handleClose}>
      Annuler
    </Button>,
    current > 0 && (
      <Button key="back" onClick={prev}>
        Précédent
      </Button>
    ),
    current < steps.length - 1 && (
      <Button 
        key="next" 
        type="primary" 
        onClick={current === 0 ? previewOrders : next}
        disabled={
          (importMethod === 'api' && (!shopifyCredentials.shopName || !shopifyCredentials.apiKey || !shopifyCredentials.password)) ||
          (importMethod === 'csv' && !csvFile) ||
          (current === 1 && previewData?.orders?.length === 0) ||
          (current === 2 && importing)
        }
      >
        {current === 0 ? 'Prévisualiser' : 'Suivant'}
      </Button>
    ),
    current === steps.length - 1 && (
      <Button key="finish" type="primary" onClick={handleFinish}>
        Terminer
      </Button>
    ),
  ];

  return (
    <Modal
      title={
        <div className="flex items-center">
          <ShopOutlined className="mr-2" />
          <span>Importer des commandes depuis Shopify</span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      width={800}
      footer={modalFooter}
    >
      <Steps current={current} className="mb-6">
        {steps.map(item => (
          <Step key={item.title} title={item.title} />
        ))}
      </Steps>
      
      <div className="steps-content">
        {steps[current].content}
      </div>
    </Modal>
  );
};

export default ImportOrdersModal; 